import { logger } from '@adornis/base/logging.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { BuddyMailAttachment } from '@adornis/bh-matching-mails/entities/attachment.js';
import { BuddyMailTemplate, getBuddyMailTemplates } from '@adornis/bh-matching-mails/entities/mail-templates.js';
import '@adornis/buchungsheld-theme/components/tr-data-view.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-empty-placeholder.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-section.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import '@adornis/chemistry/elements/components/x-toggle-content';
import { buildFullOptions } from '@adornis/data-view/client/data-view-options.js';
import { askForFile } from '@adornis/file-utils/client/helper-methods.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import './matching-mails-template-editor.js';

/*
 * @element buddy-page-mails
 */
@customElement('buddy-page-mails')
export class BuddyPageMails extends ChemistryLitElement {
  @state() private readonly _mailTemplates = new RXController(
    this,
    getBuddyMailTemplates()(selectionSet(() => BuddyMailTemplate, 2)),
  );

  override render() {
    if (!this._mailTemplates.value) {
      return html` <x-loading-state></x-loading-state> `;
    }

    return html`
      <x-flex space="lg">
        <bh-breadcrumb .goUp=${() => goTo('/')}></bh-breadcrumb>
        ${this._materialManagement()} ${this._mailManagement()}
      </x-flex>
    `;
  }

  private _mailManagement() {
    return html`
      <x-toggle-content
        open
        label="Mails verwalten"
        .content=${() =>
          html`
            <matching-mails-template-editor
              .getMailTemplates=${() => {
                return this._mailTemplates.value ?? [];
              }}
            ></matching-mails-template-editor>
          `}
      ></x-toggle-content>
    `;
  }

  private _materialManagement() {
    return html`
      <x-toggle-content
        label="Anhänge verwalten"
        .content=${() => html`
          <x-flex space="md">
            <x-flex horizontal end>
              <x-button mode="text" icon="add" @click=${() => this._addAttachment()}> Anhang hinzufügen </x-button>
            </x-flex>
            <tr-data-view
              .options=${buildFullOptions({
                class: BuddyMailAttachment,
                customColumns: [
                  {
                    fieldName: 'meta.fileName',
                    columnName: 'Dateiname',
                    renderItem: instance =>
                      html` <x-text>
                        ${instance.meta.fileName.length > 30
                          ? instance.meta.fileName.slice(0, 30) + '...'
                          : instance.meta.fileName}
                        <x-tooltip .text=${instance.meta.fileName}> </x-tooltip>
                      </x-text>`,
                  },
                  { fieldName: 'meta.extension', columnName: 'Datei-Typ' },
                  { fieldName: 'createdAt', columnName: 'Hochgeladen am' },
                  {
                    customID: '_options',
                    columnWidth: 'max-content',
                    renderItem: instance =>
                      html`
                        <x-flex horizontal crossaxis-center space="xs">
                          <x-button
                            mode="icon"
                            icon="delete"
                            tone="error"
                            @click=${async () => {
                              if (!(await TrDialog.confirm('Möchtest du diesen Anhang wirklich löschen?'))) return;
                              try {
                                await TrDialog.waitFor(instance.remove());
                                void XSnackbar.show('Anhang wurde erfolgreich gelöscht.');
                              } catch (err) {
                                logger.error({ err });
                                void TrDialog.alert(
                                  'Es gab ein Problem beim Löschen dieses Anhangs. Bitte versuchen Sie es später erneut.',
                                );
                              }
                            }}
                          ></x-button>
                        </x-flex>
                      `,
                  },
                ],
                emptyState: () =>
                  html`
                    <x-flex horizontal crossaxis-center center space="md" ${css({ gridColumn: '1 / -1' })}>
                      <x-empty-placeholder
                        no-wrapper
                        .text=${'Es gibt aktuell noch keine Anhänge. Mit dem Button "Anhang hinzufügen" kann der erste Anhang erstellt werden.'}
                      ></x-empty-placeholder>
                    </x-flex>
                  `,
              }).dataViewOptions}
            ></tr-data-view>
          </x-flex>
        `}
      >
      </x-toggle-content>
    `;
  }

  private async _addAttachment() {
    const files = await askForFile({ acceptMultipleFiles: true });
    if (!files?.length) return;

    try {
      await TrDialog.waitFor(Promise.all(Array.from(files).map(file => BuddyMailAttachment.upload(file))));
      void XSnackbar.show('Neue Anhänge wurden erfolgreich hochgeladen');
    } catch (err) {
      logger.error({ err });
      void TrDialog.alert('Es gab eine Problem beim Hochladen der Anhänge. Bitte versuchen Sie es später erneut.');
    }
  }
}
